import { STATUS_ALLOW_SELL, STATUS_CLOSED, SWAPOFFER_ID_TN_STR } from '@/src/utils/constants';
import { type PaymentKeyHash } from '@lucid-evolution/lucid';
import 'reflect-metadata';
import {
    AssetClass,
    Convertible,
    Maybe,
    StakeCredentialPubKeyHash,
    asSmartDBEntity,
    divide_By_Scaled_1e6_And_RoundDownSafe,
    multiply_By_Scaled_1e6_And_RoundUp,
    multiply_By_Scaled_BPx1e3_And_RoundUp,
    toJson,
    type CS,
} from '../Commons';
import { BaseSmartDBEntity } from './index.exports';

@asSmartDBEntity()
export class SwapOfferEntity extends BaseSmartDBEntity {
    protected static _apiRoute: string = 'swap-offer';
    protected static _className: string = 'Swap Offer';

    protected static _plutusDataIndex = 0;
    protected static _is_NET_id_Unique = false;

    // #region fields

    _NET_id_TN_Str: string = SWAPOFFER_ID_TN_STR;

    @Convertible({ isCreatedAt: true })
    createdAt!: Date;

    @Convertible({ isUpdatedAt: true })
    updatedAt!: Date;

    // #endregion fields

    // #region datum

    @Convertible({ isForDatum: true })
    sodVersion!: number;

    @Convertible({ isForDatum: true, type: String })
    sodSwapOfferPolicyID_CS!: CS;

    @Convertible({ isForDatum: true, type: String })
    sodFundPolicy_CS!: CS;

    @Convertible({ isForDatum: true, type: String })
    sodSellerPaymentPKH!: PaymentKeyHash;

    @Convertible({ isForDatum: true, type: Maybe<StakeCredentialPubKeyHash> })
    sodSellerStakePKH!: Maybe<StakeCredentialPubKeyHash>;

    @Convertible({ isForDatum: true })
    sodAskedCommission_InBPx1e3!: bigint;

    @Convertible({ isForDatum: true })
    sodAmount_FT_Available!: bigint;

    @Convertible({ isForDatum: true })
    sodAmount_ADA_Available!: bigint;

    @Convertible({ isForDatum: true })
    sodTotal_FT_Earned!: bigint;

    @Convertible({ isForDatum: true })
    sodTotal_ADA_Earned!: bigint;

    @Convertible({ isForDatum: true })
    sodAllowSellFT!: number;

    @Convertible({ isForDatum: true })
    sodAllowSellADA!: number;

    @Convertible({ isForDatum: true })
    sodSwapStatus!: number;

    @Convertible({ isForDatum: true, type: AssetClass })
    sodTokenGov_AC!: AssetClass;

    @Convertible({ isForDatum: true })
    sodRequiredTokenGov!: bigint;

    @Convertible({ isForDatum: true })
    sodMinADA!: bigint;

    // #endregion datum

    // #region  db

    public static defaultFieldsWhenUndefined: Record<string, boolean> = {};

    public static alwaysFieldsForSelect: Record<string, boolean> = {
        ...super.alwaysFieldsForSelect,
        sodSwapOfferPolicyID_CS: true,
        sodFundPolicy_CS: true,
        sodSellerPaymentPKH: true,
        sodSellerStakePKH: true,
        sodAskedCommission_InBPx1e3: true,
        sodAmount_FT_Available: true,
        sodAmount_ADA_Available: true,
        sodTotal_FT_Earned: true,
        sodTotal_ADA_Earned: true,
        sodAllowSellFT: true,
        sodAllowSellADA: true,
        sodSwapStatus: true,
        sodTokenGov_AC: true,
        sodRequiredTokenGov: true,
        sodMinADA: true,
    };

    // #endregion  db

    // #region class methods

    public getNET_id_CS(): string {
        return this.sodSwapOfferPolicyID_CS;
    }

    //----------------------------------------
    // BUY FT WITH ADA. Calculate how much FT i can get with the ADA i give
    /**
     * Calculates the total FT, commission in FT, and the net FT the user will receive,
     * given the amount of ADA they want to spend.
     */
    public static calculate_Buy_FT_from_ADA_toGive(
        amount_ADA_toGive: bigint,
        tokenFTPriceADAx1e6: bigint,
        askedCommission_InBPx1e3: bigint
    ): { total_FT: bigint; commission_FT: bigint; amount_FT_toGet: bigint } {
        //----------------------------------------
        // Step 1: Calculate Total FT (Divide ADA by Price, Round Down)
        //----------------------------------------
        // total_FT = amount_ADA_toGive / price (scaled by 1e6)
        const total_FT = divide_By_Scaled_1e6_And_RoundDownSafe(amount_ADA_toGive, tokenFTPriceADAx1e6);
        //----------------------------------------
        // Step 2: Calculate Commission in FT (Round Up)
        //----------------------------------------
        const commission_FT = multiply_By_Scaled_BPx1e3_And_RoundUp(total_FT, askedCommission_InBPx1e3);
        //----------------------------------------
        // Step 3: Calculate Net FT to Get
        //----------------------------------------
        const amount_FT_toGet = total_FT - commission_FT;
        //----------------------------------------
        console.log(
            `[${this.className()}] calculate_Buy_FT_from_ADA_toGive - ${toJson({
                giveADA: amount_ADA_toGive,
                price: tokenFTPriceADAx1e6,
                total_FT,
                commission_FT,
                getFT: amount_FT_toGet,
            })}`
        );
        //----------------------------------------
        return { total_FT, commission_FT, amount_FT_toGet };
    }

    // BUY FT WITH ADA. Calculate how much ADA i need to give to get the amount of FT i want
    public static calculate_Buy_FT_from_FT_toGet(
        amount_FT_toGet: bigint,
        tokenFTPriceADAx1e6: bigint,
        askedCommission_InBPx1e3: bigint
    ): { amount_ADA_toGive: bigint; commission_FT: bigint; total_FT: bigint } {
        //----------------------------------------
        // las comisiones son en basic points bp multiplicados por 1e3 o lo que es igual 10e2 = 1_000
        // eso significa que al valor de Commission_Rate_InBPx1e3 tengo que dividirlo por
        // 1e3 para pasarlo a bp
        // 100 para pasarlo a porcentaje normal del 1 al 100
        // 100 para pasarlo a porcentaje del 0 al 1
        // den = 1e3 * 100 * 100 = 1000 * 100 * 100 = 10_000_000
        //----------------------------------------
        // Denominator for BPx1e3 conversion = 10_000_000
        //----------------------------------------
        const BP_DENOMINATOR = 10_000_000n;
        //----------------------------------------
        // Step 1: Calculate Total FT (User Pays Total Amount to Cover Commission)
        //----------------------------------------
        // total_FT = amount_FT_toGet / (1 - commissionRate)
        const commissionRate = askedCommission_InBPx1e3;
        const denominator = BP_DENOMINATOR - commissionRate; // 1 - commissionRate (scaled)
        const total_FT = (amount_FT_toGet * BP_DENOMINATOR + denominator - 1n) / denominator; // Round up
        //----------------------------------------
        // Step 2: Calculate Commission FT
        //----------------------------------------
        const commission_FT = total_FT - amount_FT_toGet;
        //----------------------------------------
        // Step 3: Calculate ADA to Give (Round Up using scaled price)
        //----------------------------------------
        const amount_ADA_toGive = multiply_By_Scaled_1e6_And_RoundUp(total_FT, tokenFTPriceADAx1e6);
        //----------------------------------------
        console.log(
            `[${this.className()}] calculate_Buy_FT_from_FT_toGet - ${toJson({
                getFT: amount_FT_toGet,
                price: tokenFTPriceADAx1e6,
                total_FT,
                commission_FT,
                giveADA: amount_ADA_toGive,
            })}`
        );
        //----------------------------------------
        // Return Results
        //----------------------------------------
        return { total_FT, commission_FT, amount_ADA_toGive };
    }
    //----------------------------------------
    // SELL FT and GET ADA. Calculate how much ADA i can get with the FT i give
    /**
     * Calculates the total ADA to receive, commission in ADA, and net ADA amount to get
     * when selling FT. Ensures precision and rounding behavior matches Haskell logic.
     */
    public static calculate_Sell_FT_from_FT_ToGive(
        amount_FT_toGive: bigint,
        tokenFTPriceADAx1e6: bigint,
        askedCommission_InBPx1e3: bigint
    ): { total_ADA: bigint; commission_ADA: bigint; amount_ADA_toGet: bigint } {
        //----------------------------------------
        // las comisiones son en basic points bp multiplicados por 1e3 o lo que es igual 10e2 = 1_000
        // eso significa que al valor de Commission_Rate_InBPx1e3 tengo que dividirlo por
        // 1e3 para pasarlo a bp
        // 100 para pasarlo a porcentaje normal del 1 al 100
        // 100 para pasarlo a porcentaje del 0 al 1
        // den = 1e3 * 100 * 100 = 1000 * 100 * 100 = 10_000_000
        //----------------------------------------
        // Step 1: Calculate Total ADA (Round Up with scaled price)
        //----------------------------------------
        const total_ADA = multiply_By_Scaled_1e6_And_RoundUp(amount_FT_toGive, tokenFTPriceADAx1e6);
        //----------------------------------------
        // Step 2: Calculate Commission in ADA (Round Up with BPx1e3 scaling)
        //----------------------------------------
        const commission_ADA = multiply_By_Scaled_BPx1e3_And_RoundUp(total_ADA, askedCommission_InBPx1e3);
        //----------------------------------------
        // Step 3: Calculate Net ADA to Get
        //----------------------------------------
        const amount_ADA_toGet = total_ADA - commission_ADA;
        //----------------------------------------
        console.log(
            `[${this.className()}] calculate_Sell_FT_from_FT_ToGive - ${toJson({
                giveFT: amount_FT_toGive,
                price: tokenFTPriceADAx1e6,
                total_ADA,
                commission_ADA,
                getADA: amount_ADA_toGet,
            })}`
        );
        //----------------------------------------
        return { total_ADA, commission_ADA, amount_ADA_toGet };
    }
    // SELL FT and GET ADA. Calculate how much FT i need to give to get the amount of ADA i want
    /**
     * SELL FT and GET ADA. Calculate how much FT the user needs to give to get the desired amount of ADA.
     */
    public static calculate_Sell_FT_from_ADA_ToGet(
        amount_ADA_toGet: bigint,
        tokenFTPriceADAx1e6: bigint,
        askedCommission_InBPx1e3: bigint
    ): { amount_FT_toGive: bigint; commission_ADA: bigint; total_ADA: bigint } {
        //----------------------------------------
        // Step 1: Calculate Total ADA (Including Commission)
        //----------------------------------------
        // total_ADA = amount_ADA_toGet / (1 - commissionRate)
        const BP_DENOMINATOR = 10_000_000n;
        const commissionRate = askedCommission_InBPx1e3;
        const denominator = BP_DENOMINATOR - commissionRate; // (1 - commissionRate scaled)
        const total_ADA = (amount_ADA_toGet * BP_DENOMINATOR + denominator - 1n) / denominator; // Round Up
        //----------------------------------------
        // Step 2: Calculate Commission in ADA (Round Up)
        //----------------------------------------
        const commission_ADA = total_ADA - amount_ADA_toGet;
        //----------------------------------------
        // Step 3: Calculate Amount of FT to Give (Round Up)
        //----------------------------------------
        // Divide total_ADA by tokenFTPriceADAx1e6 (scaled division with rounding up)
        const amount_FT_toGive = divide_By_Scaled_1e6_And_RoundDownSafe(total_ADA, tokenFTPriceADAx1e6);
        //----------------------------------------
        console.log(
            `[${this.className()}] calculate_Sell_FT_from_ADA_ToGet - ${toJson({
                getADA: amount_ADA_toGet,
                price: tokenFTPriceADAx1e6,
                total_ADA,
                commission_ADA,
                giveFT: amount_FT_toGive,
            })}`
        );
        //----------------------------------------
        return { total_ADA, commission_ADA, amount_FT_toGive };
    }
    //----------------------------------------

    public showAllowStatus(): string {
        if (this.sodSwapStatus === STATUS_CLOSED) {
            return 'Closed';
        }
        if (this.sodAllowSellADA === STATUS_ALLOW_SELL && this.sodAllowSellFT === STATUS_ALLOW_SELL) {
            return 'Allow All Operations';
        }
        if (this.sodAllowSellADA !== STATUS_ALLOW_SELL && this.sodAllowSellFT !== STATUS_ALLOW_SELL) {
            return 'No Operations Allowed';
        }
        if (this.sodAllowSellADA === STATUS_ALLOW_SELL) {
            return 'Allow give ADA';
        }
        return 'Allow give FT';
    }

    // #endregion class methods
}
